<template>
  <div class="api-debug-panel shadow-lg" :class="{ 'is-collapsed': !isExpanded }">
    <button
      v-if="!isExpanded"
      type="button"
      class="debug-expand-button btn btn-dark"
      title="展開 Debug 視窗"
      aria-label="展開 Debug 視窗"
      @click="isExpanded = true"
    >
      ⚡
    </button>

    <!-- 控制頂列 (Header) -->
    <div v-if="isExpanded" class="api-debug-header bg-dark text-white d-flex align-items-center justify-content-between px-3 py-1">
      <div class="d-flex align-items-center gap-2">
        <span class="fw-bold text-warning">⚡ API Debug</span>
        
        <!-- 頁籤按鈕 (Tabs) -->
        <div class="btn-group btn-group-sm ms-3" role="group">
          <!-- <button @click="test()">check</button> -->
          <button 
            class="btn" 
            :class="activeTab === 'active' ? 'btn-primary' : 'btn-outline-light'"
            @click="activeTab = 'active'; isExpanded = true"
          >
            執行中
            <span class="badge bg-light text-dark ms-1">{{ activeRequests }}</span>
          </button>

          <button 
            class="btn" 
            :class="activeTab === 'error' ? 'btn-danger' : 'btn-outline-light'"
            @click="activeTab = 'error'; isExpanded = true"
          >
            錯誤區
            <span class="badge bg-light text-dark ms-1">{{ errorRequests }}</span>
          </button>
        </div>
      </div>

      <!-- 右側展開/縮放按鈕 -->
      <div>
        <button class="btn btn-sm btn-outline-secondary text-white border-0" @click="isExpanded = !isExpanded">
          <span v-if="isExpanded">▼ 縮小</span>
          <span v-else">▲ 展開 Debug 視窗</span>
        </button>
      </div>
    </div>

    <!-- 列表主體 (Body) -->
    <div v-if="isExpanded" class="api-debug-body bg-white border-top">
      <div class="table-responsive">
        <table class="table table-sm table-hover table-striped align-middle mb-0" style="font-size: 13px;">
          <thead class="table-light sticky-top">
            <tr>
              <th scope="col" style="width: 25%;">url</th>
              <th scope="col" style="width: 80px;">method</th>
              <th scope="col" style="width: 90px;">status</th>
              <th scope="col" style="width: 120px;">uuid</th>
              <th scope="col">bodydata</th>
              <th scope="col" style="width: 100px;">Time</th>
            </tr>
          </thead>
          <tbody>
            <!-- 無資料提示 -->
            <tr v-if="currentList.length === 0">
              <td colspan="6" class="text-center py-3 text-muted">目前沒有 {{ activeTab === 'active' ? '執行中' : '錯誤' }} 的 API 請求</td>
            </tr>

            <!-- API 列表 -->
            <tr v-for="item in currentList" :key="item.uuid">
              <!-- URL -->
              <td class="text-break " :title="item.url"  @click="openModal(item.url)"  style="max-width: 300px;" >
                <div class="ellipsis-1">{{ item.url }}</div>
              </td>
              
              <!-- Method -->
              <td>
                <span class="badge" :class="getMethodBadge(item.method)">{{ item.method }}</span>
              </td>
              
              <!-- Status -->
              <td>
                <span class="badge" :class="getStatusBadge(item.status)" > 
                  {{ item.status || 'Pending...' }}
                </span>
              </td>
              
              <!-- UUID -->
              <td class="font-monospace text-muted small">
                <div class="ellipsis-1">{{ item.uuid }}</div>
              </td>
              
              <!-- BodyData (單行溢出省略，點擊開啟 Detail) -->
              <td>
                <div 
                  class="bodydata-cell text-truncate text-primary" 
                  style="max-width: 300px; cursor: pointer;" 
                  :title="'點擊查看完整內容'"
                  @click="openModal(item.bodydata)"
                >
                  {{ formatBodyPreview(item.bodydata) }}
                </div>
              </td>
              
              <!-- Time (耗時) -->
              <td class="font-monospace">{{ item.time ? item.time + ' ms' : '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- BodyData 查看詳情彈窗 (Modal) -->
    <div v-if="selectedBody" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header py-2">
            <h6 class="modal-title fw-bold">Body Data 詳情</h6>
            <button type="button" class="btn-close" @click="selectedBody = null"></button>
          </div>
          <div class="modal-body bg-light">
            <pre class="mb-0 font-monospace p-2 bg-white rounded border" style="max-height: 400px; font-size: 12px;">{{ formatJson(selectedBody) }}</pre>
          </div>
          <div class="modal-footer py-1">
            <button type="button" class="btn btn-sm btn-secondary" @click="selectedBody = null">關閉</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import usePub from "~/store/Public"
import _ from "lodash";
// 控制介面狀態
const isExpanded = ref(false)
const activeTab = ref('active') // 'active' | 'error'
const selectedBody = ref(null)
// const requests=computed(()=>{
//   return _.orderBy(_.map(usePub().responseObj[activeTab],(item)=>{return item}),["time"],["asc"]);
// })
// API 紀錄資料集（可整合至 Pinia 或全域 Event Bus 寫入）
// const requests = ref([
//   {
//     uuid: 'a1b2c3d4',
//     url: '/api/v1/travel/orders/create',
//     method: 'POST',
//     status: null, // 執行中
//     bodydata: { name: '張三', amount: 53900, items: ['東京5日遊'] },
//     time: null
//   },
//   {
//     uuid: 'e5f6g7h8',
//     url: '/api/v1/passport/ocr-parse',
//     method: 'POST',
//     status: 500,
//     bodydata: { error: 'Internal Server Error', message: 'OCR Timeout' },
//     time: 1420
//   },
//   {
//     uuid: 'i9j0k1l2',
//     url: '/api/v1/receipt/generate',
//     method: 'GET',
//     status: 404,
//     bodydata: { message: 'Receipt ID Not Found' },
//     time: 310
//   }
// ])

// 依頁籤過濾清單
const activeRequests = computed(() => _.keys(usePub().responseObj.active).length)
const errorRequests = computed(() => usePub().responseObj.error.length)

const currentList = computed(() => {
  return _.orderBy(_.map(usePub().responseObj[activeTab.value],(item)=>{return item}),["time"],["asc"]);
})

// 格式化單行預覽
const formatBodyPreview = (data) => {
  if (!data) return '-'
  return typeof data === 'object' ? JSON.stringify(data) : String(data)
}

// 格式化排版 JSON
const formatJson = (data) => {
  if (typeof data === 'string') {
    try { data = JSON.parse(data) } catch (e) { return data }
  }
  return JSON.stringify(data, null, 2)
}

// 開啟彈窗
const openModal = (data) => {
  if (data) selectedBody.value = data
}

// Badge 樣式對應
const getMethodBadge = (method) => {
  const map = { GET: 'bg-info text-dark', POST: 'bg-success', PUT: 'bg-warning text-dark', DELETE: 'bg-danger' }
  return map[method?.toUpperCase()] || 'bg-secondary'
}

const getStatusBadge = (status) => {
  if (!status) return 'bg-secondary'
  if (status >= 400) return 'bg-danger'
  if (status >= 200) return 'bg-success'
  return 'bg-secondary'
}
const test =()=>{
  console.log(usePub().responseObj,_.orderBy(_.map(usePub().responseObj[activeTab],(item)=>{return item}),["time"],["asc"]));
}
</script>

<style scoped>
/* 釘選在網頁最下方 */
.api-debug-panel {
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 1080;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.api-debug-panel:not(.is-collapsed) {
  width: min(100vw, 1100px);
}

.debug-expand-button {
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 0 4px 0 0;
}

/* 高度限制與滾動條 */
.api-debug-body {
  max-height: 280px;
  overflow-y: auto;
}

.bodydata-cell:hover {
  text-decoration: underline;
}
</style>